'use client'

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react'

type Web3ReadyValue = {
  ready: boolean
  setReady: (value: boolean) => void
  /** Trigger the Web3 chunk to load. Safe to call multiple times — loads only once. */
  activate: () => void
  /** Called by DeferredWeb3Wrapper to register its load function. Internal use only. */
  _registerActivate: (fn: () => void) => void
}

const Web3ReadyContext = createContext<Web3ReadyValue | null>(null)

export function Web3ReadyProvider({ children }: PropsWithChildren) {
  const [ready, setReadyState] = useState(false)
  const activateFnRef = useRef<(() => void) | null>(null)
  const pendingRef = useRef(false)
  const activatedRef = useRef(false)

  const setReady = useCallback((value: boolean) => setReadyState(value), [])

  const _registerActivate = useCallback((fn: () => void) => {
    activateFnRef.current = fn
    if (pendingRef.current) {
      pendingRef.current = false
      fn()
    }
  }, [])

  const activate = useCallback(() => {
    if (activatedRef.current) return
    activatedRef.current = true
    if (activateFnRef.current) {
      activateFnRef.current()
    } else {
      // DeferredWeb3Wrapper hasn't mounted yet — flag it to load immediately on register
      pendingRef.current = true
    }
  }, [])

  return (
    <Web3ReadyContext.Provider value={{ ready, setReady, activate, _registerActivate }}>
      {children}
    </Web3ReadyContext.Provider>
  )
}

export function useWeb3Ready(): Web3ReadyValue | null {
  return useContext(Web3ReadyContext)
}
